import { type DocumentNode, gql } from '@apollo/client';

/**
 * Variables for updateProfile mutation
 */
export interface UpdateProfileVariables {
  profileId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  phoneNumber?: string;
  companyName?: string;
  password1?: string;
  password2?: string;
}

/**
 * User structure in updated profile
 */
export interface UpdatedProfileUser {
  id: string;
  username: string;
  email: string;
}

/**
 * Media group node structure
 */
export interface UpdateProfileMediaGroupNode {
  id: string;
  name: string;
}

/**
 * Media group edge structure
 */
export interface UpdateProfileMediaGroupEdge {
  node: UpdateProfileMediaGroupNode;
}

/**
 * Media groups connection structure
 */
export interface UpdateProfileMediaGroupsConnection {
  edges: UpdateProfileMediaGroupEdge[];
}

/**
 * Permissions group structure in updated profile
 */
export interface UpdateProfilePermissionsGroup {
  id: string;
  name: string;
  description: string | null;
  mediaGroups: UpdateProfileMediaGroupsConnection;
}

/**
 * Media permission node in updated profile
 */
export interface UpdateProfileMediaPermissionNode {
  id: string;
  permissionsGroup: UpdateProfilePermissionsGroup;
}

/**
 * Media permission edge in updated profile
 */
export interface UpdateProfileMediaPermissionEdge {
  node: UpdateProfileMediaPermissionNode;
}

/**
 * Media permission set connection in updated profile
 */
export interface UpdateProfileMediaPermissionSetConnection {
  edges: UpdateProfileMediaPermissionEdge[];
}

/**
 * Updated profile structure
 */
export interface UpdatedProfile {
  id: string;
  user: UpdatedProfileUser;
  role: string | null;
  phoneNumber: string | null;
  companyName: string | null;
  thirdPartyEmail: string | null;
  pageSize: number | null;
  pageSizeApp: number | null;
  defaultTab: string | null;
  lastlogin: string | null;
  ip: string | null;
  lastPasswordChange: string | null;
  resumeEnabled: boolean;
  resumeUnder5: boolean;
  textOnly: boolean;
  dateGmtName: string | null;
  dateUtcOffset: string | null;
  expiration: string | null;
  authenticateWithCas: boolean;
  authenticateWithSaml: boolean;
  mediapermissionSet: UpdateProfileMediaPermissionSetConnection;
}

/**
 * Update profile payload structure
 */
export interface UpdateProfilePayload {
  profile: UpdatedProfile;
}

/**
 * Response structure for updateProfile mutation
 */
export interface UpdateProfileResponse {
  updateProfile: UpdateProfilePayload;
}

const updateProfile: DocumentNode = gql`
  mutation update_user(
    $profileId: ID!
    $email: String
    $firstName: String
    $lastName: String
    $role: String
    $phoneNumber: String
    $companyName: String
    $password1: String
    $password2: String
  ) {
    updateProfile(
      profileId: $profileId
      email: $email
      firstName: $firstName
      lastName: $lastName
      role: $role
      phoneNumber: $phoneNumber
      companyName: $companyName
      password1: $password1
      password2: $password2
    ) {
      profile {
        id
        user {
          id
          username
          email
        }
        role
        phoneNumber
        companyName
        thirdPartyEmail
        pageSize
        pageSizeApp
        defaultTab
        lastlogin
        ip
        lastPasswordChange
        resumeEnabled
        resumeUnder5
        textOnly
        dateGmtName
        dateUtcOffset
        expiration
        authenticateWithCas
        authenticateWithSaml
        mediapermissionSet {
          edges {
            node {
              id
              permissionsGroup {
                id
                name
                description
                mediaGroups {
                  edges {
                    node {
                      id
                      name
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default updateProfile;
